import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialAbout } from "../data/initialData";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, duration: 0.7 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, type: "spring", stiffness: 80 } }
};

const focusAreas = [
  "Natural Language Processing",
  "Language AI Infrastructure",
  "Speech Technologies",
  "Machine Translation",
  "Language Resource Development",
  "AI Platform Engineering",
  "Dataset Engineering",
  "MLOps Architecture"
];

const About = () => {
  const [aboutData] = useLocalStorage('about', initialAbout);

  return (
    <motion.section
      id="about"
      className="py-24 relative overflow-hidden bg-slate-950"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <motion.div
        className="absolute top-20 right-20 w-72 h-72 bg-cyan-500/5 rounded-full blur-[100px]"
        animate={{ x: [0, -50, 0], y: [0, 30, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="section-container relative z-10">
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
            variants={itemVariants}
          >
            About
          </motion.span>
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" variants={itemVariants}>
            AI Engineer & <span className="gradient-text">Language Technologist</span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div variants={itemVariants} className="space-y-6">
            {aboutData.description.split('\n\n').map((paragraph, i) => (
              <p key={i} className="text-slate-300 leading-relaxed text-base">
                {paragraph}
              </p>
            ))}
            <motion.blockquote
              className="border-l-2 border-cyan-500/50 pl-6 py-2 text-cyan-300/80 italic text-sm"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              {aboutData.mission}
            </motion.blockquote>
          </motion.div>

          <motion.div variants={itemVariants}>
            <div className="glass-card rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-white mb-6 font-mono">
                Focus Areas
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {focusAreas.map((area) => (
                  <div
                    key={area}
                    className="flex items-center gap-2 text-sm text-slate-300"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                    {area}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 glass-card rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-white mb-4 font-mono">
                Current Work
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Building the Zimbabwe Indigenous Language Platform (ZILP) — a production-grade AI infrastructure platform for developing, managing, annotating, and deploying indigenous language resources and AI models across Zimbabwe and Africa.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default About;
